namespace $.$$ {

	export class $shm_hitalama_process_view extends $.$shm_hitalama_process_view {

		host(): object | null {
			const owner = this.$.$mol_owning_get( this ) as
				| $mol_wire_fiber< object, readonly unknown[], unknown >
				| null

			return owner?.host ?? null
		}

		promise_str() {
			const waiting = this.last()?.data().waiting
			if( waiting ) return waiting
			const promise = this.last()?.data()?.promise as any
			return String(promise ?? '')
		}

		method_name(): string {
			return ''
		}

		method(): Function | null {
			const host = this.host() as Record< string, unknown > | null
			const method_name = this.method_name()
			if( !host || !method_name ) return null

			const method = host[ method_name ]
			return typeof method === 'function' ? method : null
		}

		@ $mol_mem
		calls() {
			const host = this.host()
			const method = this.method()
			if( !host || !method ) return [] as $shm_hitalama_process< any >[]

			return [ ... this.$.$shm_hitalama_process.calls( host, method ) ].reverse()
		}

		@ $mol_mem
		last() {
			return this.calls()[0]
		}

		method_title() {
			const host = this.host()
			const method = this.method()
			if( !host || !method ) return '-'

			return `${ host }.${ this.$.$shm_hitalama_process.method_name( method ) }`
		}

		calls_count() {
			return String( this.calls().length )
		}

		state() {
			return this.last()?.state()
		}

		result() {
			return this.last()?.data().result
		}

		status() {
			return this.last()?.data().status ?? ''
		}

		duration() {
			const process = this.last()
			if( !process ) return '-'

			return this.duration_text( process )
		}

		started_at() {
			const data = this.last()?.data()
			return data?.started_at?.toString( 'hh:mm:ss' ) ?? ''
		}

		finished_at() {
			const data = this.last()?.data()
			return data?.finished_at?.toString( 'hh:mm:ss' ) ?? ''
		}

		error_message() {
			const error = this.last()?.data().error
			return error === undefined ? '' : this.$.$mol_error_message( error )
		}

		empty_visible() {
			return this.calls().length ? [] : super.empty_visible()
		}

		running_fiber_visible() {
			const data = this.last()?.data()
			return data?.status === 'running' && data.promise ? super.running_fiber_visible() : []
		}

		running_fiber_trace() {
			const data = this.last()?.data()
			if( data?.status !== 'running' || !data.promise ) return ''

			const graph_trace = data.fiber && this.running_fiber_graph_trace( data.fiber )
			if( graph_trace ) return this.running_fiber_trace_text( graph_trace )

			const promise = data.promise as PromiseLike< unknown > & {
				stack?: unknown
				[ Symbol.toStringTag ]?: unknown
			}

			const stack = promise.stack
			if( typeof stack === 'string' && stack ) return this.running_fiber_trace_text( stack )

			const tag = promise[ Symbol.toStringTag ]
			if( typeof tag === 'string' && tag ) return `Promise in ${ tag }`

			return `No promise stack\n${ String( data.promise ) }`
		}

		running_fiber_trace_text( stack: string ) {
			const runs_count = this.last()?.data().runs_count ?? 0
			const restarts_count = Math.max( 0, runs_count - 1 )

			return [
				'```text',
				`Executions: ${ runs_count }`,
				`Restarts: ${ restarts_count }`,
				'',
				stack,
				'```',
			].join( '\n' )
		}

		running_fiber_graph_trace( root: $mol_wire_fiber< any, any, any > ) {
			const trace = this.running_fiber_trace_path( root )
			return trace?.map( ( fiber, index )=> {
				const prefix = index ? '-> ' : ''
				return prefix + this.running_fiber_trace_line( fiber )
			} ).join( '\n' ) ?? ''
		}

		running_fiber_trace_line( fiber: $mol_wire_fiber< any, any, any > ) {
			const args = fiber.args
			if( !args.length ) return String( fiber )

			return `${ fiber }\n   args: ${ this.running_fiber_args_text( args ) }`
		}

		running_fiber_args_text( args: readonly unknown[] ) {
			return `[ ${ args.map( arg => this.running_fiber_arg_text( arg ) ).join( ', ' ) } ]`
		}

		running_fiber_arg_text( value: unknown, seen = new Set< unknown >() ): string {
			if( typeof value === 'string' ) return JSON.stringify( value.length > 160 ? value.slice( 0, 157 ) + '...' : value )
			if( typeof value === 'number' || typeof value === 'boolean' || value === null ) return String( value )
			if( typeof value === 'bigint' ) return `${ value }n`
			if( value === undefined ) return 'undefined'
			if( typeof value === 'symbol' ) return String( value )
			if( typeof value === 'function' ) return `[Function ${ this.$.$mol_func_name( value ) }]`
			if( typeof value !== 'object' ) return String( value )

			if( seen.has( value ) ) return '[Circular]'
			seen.add( value )

			const tag = ( value as { [ Symbol.toStringTag ]?: unknown } )[ Symbol.toStringTag ]
			if( typeof tag === 'string' && tag ) return tag

			if( Array.isArray( value ) ) {
				const items = value.slice( 0, 6 ).map( item => this.running_fiber_arg_text( item, seen ) )
				if( value.length > items.length ) items.push( '...' )
				return `[ ${ items.join( ', ' ) } ]`
			}

			const title = ( value as { constructor?: { name?: string } } ).constructor?.name
			const keys = Object.keys( value ).slice( 0, 6 )
			if( !keys.length ) return title && title !== 'Object' ? title : '{}'

			const fields = keys.map( key => {
				const next = ( value as Record< string, unknown > )[ key ]
				return `${ key }: ${ this.running_fiber_arg_text( next, seen ) }`
			} )
			if( Object.keys( value ).length > keys.length ) fields.push( '...' )

			return `${ title && title !== 'Object' ? title + ' ' : '' }{ ${ fields.join( ', ' ) } }`
		}

		running_fiber_trace_path(
			fiber: $mol_wire_fiber< any, any, any >,
			seen = new Set< $mol_wire_fiber< any, any, any > >,
		): $mol_wire_fiber< any, any, any >[] | null {
			if( seen.has( fiber ) ) return null
			seen.add( fiber )

			for( const pub of fiber.pub_list ) {
				if( !( pub instanceof $mol_wire_fiber ) ) continue

				const path = this.running_fiber_trace_path( pub, seen )
				if( path ) return [ fiber, ... path ]
			}

			if( fiber.incompleted ) return [ fiber ]

			return null
		}

		@ $mol_mem
		call_cards() {
			return this.calls().map( ( _, index )=> this.Call_card( index ) )
		}

		process( index: number ) {
			return this.calls()[ index ]
		}

		call_status( index: number ) {
			return this.process( index )?.data().status ?? 'pending'
		}

		call_name( index: number ) {
			const process = this.process( index )
			if( !process ) return '-'

			const args = process.args.map( arg => this.short_value( arg ) ).join( ', ' )
			return `${ process.method_name }(${ args })`
		}

		call_duration( index: number ) {
			const process = this.process( index )
			if( !process ) return '-'

			return this.duration_text( process )
		}

		call_time( index: number ) {
			const data = this.process( index )?.data()
			if( !data ) return '-'

			const started = data.started_at?.toString( 'hh:mm:ss' ) ?? '-'
			const finished = data.finished_at?.toString( 'hh:mm:ss' ) ?? '-'

			return `${ started } -> ${ finished }`
		}

		call_args_visible( index: number ) {
			return this.process( index )?.args.length ? super.call_args_visible( index ) : []
		}

		call_args( index: number ) {
			return this.process( index )?.args ?? []
		}

		call_result_visible( index: number ) {
			return this.process( index )?.data().result === undefined ? [] : super.call_result_visible( index )
		}

		call_result( index: number ) {
			return this.process( index )?.data().result ?? null
		}

		call_error_visible( index: number ) {
			return this.process( index )?.data().error === undefined ? [] : super.call_error_visible( index )
		}

		call_error( index: number ) {
			return this.process( index )?.data().error ?? null
		}

		duration_text( process: $shm_hitalama_process< any > ) {
			if( process.data().status === 'running' ) this.$.$mol_state_time.now( 250 )

			const duration = process.duration()
			if( duration < 1000 ) return `${ Math.round( duration ) } ms`
			if( duration < 60000 ) return `${ ( duration / 1000 ).toFixed( 1 ) } s`

			const minutes = Math.floor( duration / 60000 )
			const seconds = Math.floor(( duration - minutes * 60000 ) / 1000 )
			return `${ minutes } min ${ seconds } s`
		}

		short_value( value: unknown ) {
			if( typeof value === 'string' ) return JSON.stringify( value.length > 24 ? value.slice( 0, 21 ) + '...' : value )
			if( typeof value === 'number' || typeof value === 'boolean' || value === null ) return String( value )
			if( value === undefined ) return 'undefined'

			const title = ( value as { constructor?: { name?: string } } )?.constructor?.name
			return title && title !== 'Object' ? title : '{...}'
		}

	}

}
